import { guardArray } from "@msuite/utils/array";
import type { PropsWithChildren } from "react";
import { BlankSlate } from "../blank-slate";
import { useTranslation } from "../localization";
import { invalidate, type QueryKey, useQueryClient } from "../query";
import { cn } from "../utils";
import { DashboardTileListContext, useDashboardTileListContext } from "./context";
import { DashboardTileListItem } from "./dashboard-tile-list-item";

/** Props Interface */
interface DashboardTileListProps<T> extends PropsWithChildren {
	isLoading: boolean;
	data: Array<T> | undefined;
	queryKey: QueryKey;
}

function DashboardTileList<T>({
	children,
	data = [],
	isLoading = false,
	queryKey,
}: DashboardTileListProps<T>) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const hasItems = guardArray(data).length > 0;

	/** Functions */
	async function refetch() {
		await qc.invalidateQueries({
			predicate: invalidate(guardArray(queryKey) as unknown as string[]),
		});
	}

	/** Render */
	return (
		<DashboardTileListContext.Provider value={{ queryKey, refetch }}>
			<div
				className={cn(
					"w-[calc(100%+3rem)] h-full flex-col gap-0 overflow-y-auto shrink flex -mb-4 pb-0",
					"-ml-6",
					"relative",
					"border-t-[0.5px] border-color",
				)}
			>
				{isLoading ? (
					<BlankSlate
						isLoading
						withAccessory={false}
						withBackground={false}
						withBorder={false}
						label={`${t("general.loading")}...`}
					/>
				) : hasItems ? (
					children
				) : (
					<BlankSlate withAccessory={false} />
				)}
			</div>
		</DashboardTileListContext.Provider>
	);
}

DashboardTileList.Item = DashboardTileListItem;
DashboardTileList.useContext = useDashboardTileListContext;

export { DashboardTileList };
